import i18n from "../i18n";
import React, { useState } from "react";
import { api, apiFileUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { farmDateIso } from "@/lib/farmTime";
import { useAuth } from "@/context/AuthContext";

const today = () => farmDateIso();

function Row({ label, children }) {
    return (
        <div>
            <div className="text-xs text-muted-foreground mb-1">{label}</div>
            {children}
        </div>
    );
}

/**
 * Universal add-event dialog for the AnimalProfilePage.
 * kind: 'treatment' | 'weight' | 'movement' | 'mortality' | 'reservation' | 'vaccination' | 'health' | 'expense' |
 *       'breeding' | 'pregnancy_check' | 'gestation' | 'farrowing' | 'feeding' | 'water' | 'observation'
 */
export default function AddEventDialog({ kind, animalId, onSaved, onClose }) {
    const [f, setF] = useState(defaultsFor(kind, animalId));
    const [busy, setBusy] = useState(false);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const { isOwner } = useAuth();
    const ownerMode = Boolean(isOwner());
    const set = (k, v) => setF({ ...f, [k]: v });

    const meta = KIND_META[kind];
    if (!meta) return null;

    const save = async () => {
        const validation = meta.validate ? meta.validate(f) : null;
        if (validation) { toast.error(validation); return; }
        setBusy(true);
        try {
            const payload = meta.transform ? meta.transform(f) : { ...f };

            if (!ownerMode && ["treatment", "vaccination", "deworming", "vet_visit", "health"].includes(kind)) {
                delete payload.cost;
                delete payload.amount;
                delete payload.financial_loss;
            }

            if (meta.allowPhoto && photoFile) {
                const fd = new FormData();
                fd.append("file", photoFile);
                const upload = await api.post("/upload", fd);
                const stored = upload.data || {};
                payload.photo_file_id = stored.id || "";
                payload.photo_filename = stored.original_filename || photoFile.name;
                payload.photo_url = apiFileUrl(stored.id);
            }

            if (meta.allowPhoto && !photoFile && photoPreview) {
                payload.photo_data_url = photoPreview;
            }

            await api.post(`/${meta.path}`, payload);
            toast.success(`${meta.title} saved`);
            onSaved && onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-testid={`add-${kind}-dialog`}>
                <DialogHeader>
                    <DialogTitle>{meta.title}</DialogTitle>
                    <DialogDescription>{meta.description}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                        {meta.allowPhoto && (
                            <div className="space-y-2 rounded-md border p-3">
                                <Row label={i18n.t("batch4.164761c4bd7d")}>
                                    <Input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = (e.target.files && e.target.files[0]) ? e.target.files[0] : null;
                                            setPhotoFile(file);
                                            if (!file) {
                                                setPhotoPreview("");
                                                return;
                                            }
                                            const reader = new FileReader();
                                            reader.onload = () => setPhotoPreview(String(reader.result || ""));
                                            reader.readAsDataURL(file);
                                        }}
                                    />
                                </Row>
                                {photoPreview ? <img src={photoPreview} alt={i18n.t("batch4.1aa787fe0cfb")} className="h-28 w-full rounded-md border object-cover" /> : null}
                            </div>
                        )}
                    {meta.fields(f, set, { isOwner: ownerMode })}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>{i18n.t("batch4.77dfd2135f4d")}</Button>
                    <Button onClick={save} disabled={busy} data-testid={`save-${kind}-btn`}>{i18n.t("batch4.efc007a393f6")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function defaultsFor(kind, aid) {
    const base = { animal_id: aid, date: today() };
    if (kind === "treatment") return { ...base, issue: "", diagnosis: "", medicine: "", dosage: "", route: "oral", reason: "", start_date: today(), end_date: "", administered_by: "", withdrawal_end_date: "", cost: "", status: "under_treatment", recovery_status: "under_treatment", vet_notes: "", notes: "" };
    if (kind === "breeding") return { ...base, dam_id: aid, sire_id: "", heat_date: "", service_date: today(), method: "natural", technician: "", expected_farrowing_date: "", notes: "" };
    if (kind === "pregnancy_check") return { ...base, dam_id: aid, date: today(), result: "pregnant", estimated_due_date: "", notes: "" };
    if (kind === "gestation") return { ...base, animal_id: aid, expected_farrowing_date: "", gestation_status: "active", notes: "" };
    if (kind === "farrowing") return { ...base, dam_id: aid, date: today(), live_born: "", stillborn: "", mummified: "", weak_piglets: "", birth_weight: "", complications: "", assistance_required: "", notes: "" };
    if (kind === "weight") return { ...base, weight: "", body_condition: "", method: "scale", notes: "" };
    if (kind === "feeding") return { ...base, feed_type: "", feed_kg: "", unit: "kg", notes: "" };
    if (kind === "water") return { ...base, location: "", water_quality: "good", amount: "", unit: "L", notes: "" };
    if (kind === "observation") return { ...base, type: "observation", issue: "", description: "", status: "recorded", notes: "" };
    if (kind === "movement") return { ...base, from_location: "", to_location: "", reason: "", staff: "", notes: "" };
    if (kind === "mortality") return { ...base, cause: "", suspected: "", disposal_method: "buried", financial_loss: "", notes: "" };
    if (kind === "reservation") return { ...base, buyer_name: "", buyer_phone: "", price: "", deposit: "", target_delivery_date: "", notes: "" };
    if (kind === "vaccination") return { animal_id: aid, scheduled_date: today(), date_given: today(), vaccine: "", vaccine_name: "", batch_number: "", manufacturer: "", expiry_date: "", next_due_date: "", administrator: "", dose: "1", route: "im", cost: "", status: "completed", required: true, notes: "" };
    if (kind === "vaccination_schedule") return { animal_id: aid, schedule_type: "vaccination", schedule_name: "", dose: "1", route: "im", scheduled_date: today(), repeat_interval_days: "", next_due_date: "", assigned_employee_id: "", assigned_employee_name: "", priority: "medium", inventory_item_id: "", quantity_used: "", notes: "" };
    if (kind === "treatment_schedule") return { animal_id: aid, schedule_type: "treatment", schedule_name: "", dose: "", route: "oral", scheduled_date: today(), repeat_interval_days: "", next_due_date: "", assigned_employee_id: "", assigned_employee_name: "", priority: "medium", inventory_item_id: "", quantity_used: "", notes: "" };
    if (kind === "health") return { ...base, issue: "", symptoms: "", diagnosis: "", body_condition: "", temperature: "", status: "under_observation", recovery_status: "under_observation", vet_notes: "", notes: "" };
    if (kind === "deworming") return { ...base, treatment_type: "deworming", medicine_name: "", dosage: "", route: "oral", reason: "", start_date: today(), end_date: "", administered_by: "", withdrawal_end_date: "", notes: "" };
    if (kind === "vet_visit") return { ...base, treatment_type: "vet_visit", visit_type: "consultation", veterinarian: "", findings: "", treatment_plan: "", follow_up_date: "", administered_by: "", notes: "" };
    if (kind === "expense") return { ...base, category: "feed", amount: "", description: "" };
    return base;
}

const KIND_META = {
    feeding: {
        title: "Record Feeding", description: "Add feeding history for this animal without changing unrelated inventory.",
        path: "feeding-records",
        validate: (f) => (!String(f.feed_type || "").trim() ? "Feed type is required" : !f.feed_kg || Number(f.feed_kg) <= 0 ? "Feed quantity must be greater than 0" : null),
        transform: (f) => ({ ...f, feed_type: String(f.feed_type).trim(), feed_kg: Number(f.feed_kg), quantity: Number(f.feed_kg), unit: f.unit || "kg", feed_unit: f.unit || "kg", source_module: "animal_profile" }),
        fields: (f, set) => (
            <>
                <Row label="Date"><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Row>
                <Row label="Feed type"><Input value={f.feed_type} onChange={(e) => set("feed_type", e.target.value)} data-testid="feeding-type-input" /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label="Quantity"><Input type="number" min="0" step="0.01" value={f.feed_kg} onChange={(e) => set("feed_kg", e.target.value)} data-testid="feeding-quantity-input" /></Row>
                    <Row label="Unit"><Input value={f.unit} onChange={(e) => set("unit", e.target.value)} /></Row>
                </div>
                <Row label="Notes"><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    water: {
        title: "Record Water Check", description: "Log water availability, quality, or an animal-specific observation.",
        path: "water-checks",
        validate: (f) => (!String(f.location || "").trim() ? "Water location is required" : null),
        transform: (f) => ({ ...f, location: String(f.location).trim(), amount: f.amount === "" ? null : Number(f.amount), source_module: "animal_profile" }),
        fields: (f, set) => (
            <>
                <Row label="Date"><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Row>
                <Row label="Location / drinker"><Input value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="water-location-input" /></Row>
                <Row label="Water quality"><Select value={f.water_quality} onValueChange={(v) => set("water_quality", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["good", "needs_attention", "contaminated", "unavailable"].map((v) => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label="Amount (optional)"><Input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => set("amount", e.target.value)} /></Row>
                    <Row label="Unit"><Input value={f.unit} onChange={(e) => set("unit", e.target.value)} /></Row>
                </div>
                <Row label="Notes"><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    observation: {
        title: "Add Observation", description: "Add a dated note to this animal's permanent activity history.",
        path: "health-records",
        validate: (f) => (!String(f.issue || f.description || f.notes || "").trim() ? "Observation is required" : null),
        transform: (f) => ({ ...f, type: "observation", issue: String(f.issue || f.description || f.notes).trim(), description: String(f.description || f.issue || f.notes).trim(), status: "recorded" }),
        fields: (f, set) => (
            <>
                <Row label="Date"><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Row>
                <Row label="Observation"><Textarea rows={3} value={f.issue} onChange={(e) => set("issue", e.target.value)} data-testid="observation-input" /></Row>
                <Row label="Additional notes"><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    breeding: {
        title: "Record Breeding", description: "Log dam/sire pairing and service details.",
        path: "breeding-records",
        validate: (f) => {
            if (!f.dam_id) return "Female animal (dam) is required";
            if (!f.sire_id) return "Male animal (sire) is required";
            if (String(f.dam_id).trim() === String(f.sire_id).trim()) return "Male and female cannot be the same animal";
            if (!f.service_date) return "Service date is required";
            return null;
        },
        transform: (f) => ({
            animal_id: f.dam_id,
            dam_id: f.dam_id,
            sire_id: f.sire_id,
            heat_date: f.heat_date || "",
            service_date: f.service_date,
            method: f.method || "natural",
            technician: f.technician || "",
            expected_farrowing_date: f.expected_farrowing_date || "",
            notes: f.notes || "",
        }),
        fields: (f, set, { isOwner }) => (
            <>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.55acfb2bd127")}><Input value={f.dam_id} onChange={(e) => set("dam_id", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.fba889c720a6")}><Input value={f.sire_id} onChange={(e) => set("sire_id", e.target.value)} /></Row>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.492c257d99c4")}><Input type="date" value={f.service_date} onChange={(e) => set("service_date", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.64dfd68f1226")}><Input type="date" value={f.heat_date || ""} onChange={(e) => set("heat_date", e.target.value)} /></Row>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.88306943fea7")}><Select value={f.method || "natural"} onValueChange={(v) => set("method", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["natural", "ai", "embryo_transfer", "other"].map((m) => <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></Row>
                    <Row label={i18n.t("batch4.e6d03272a321")}><Input value={f.technician || ""} onChange={(e) => set("technician", e.target.value)} /></Row>
                </div>
                <Row label={i18n.t("batch4.d858aa5abb0a")}><Input type="date" value={f.expected_farrowing_date || ""} onChange={(e) => set("expected_farrowing_date", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    pregnancy_check: {
        title: "Pregnancy Check", description: "Record pregnancy confirmation result.",
        path: "pregnancy-checks",
        validate: (f) => (!f.dam_id ? "Animal ID is required" : !f.date ? "Check date is required" : null),
        transform: (f) => ({
            animal_id: f.dam_id,
            dam_id: f.dam_id,
            date: f.date,
            result: f.result || "pregnant",
            estimated_due_date: f.estimated_due_date || "",
            notes: f.notes || "",
        }),
        fields: (f, set, { isOwner }) => (
            <>
                <Row label={i18n.t("batch4.646db013dd91")}><Input value={f.dam_id} onChange={(e) => set("dam_id", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.dfb1cdc86ff6")}><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.5faa59d4bc37")}><Select value={f.result || "pregnant"} onValueChange={(v) => set("result", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["pregnant", "uncertain", "not_pregnant", "abortion"].map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></Row>
                </div>
                <Row label={i18n.t("batch4.139dd46acc13")}><Input type="date" value={f.estimated_due_date || ""} onChange={(e) => set("estimated_due_date", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    gestation: {
        title: "Gestation Record", description: "Track expected farrowing and gestation status.",
        path: "gestation-records",
        validate: (f) => (!f.animal_id ? "Animal ID is required" : !f.expected_farrowing_date ? "Expected farrowing date is required" : null),
        transform: (f) => ({
            animal_id: f.animal_id,
            expected_farrowing_date: f.expected_farrowing_date,
            gestation_status: f.gestation_status || "active",
            notes: f.notes || "",
        }),
        fields: (f, set) => (
            <>
                <Row label={i18n.t("batch4.b16a33824cdb")}><Input value={f.animal_id} onChange={(e) => set("animal_id", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.3bcc72e9a172")}><Input type="date" value={f.expected_farrowing_date || ""} onChange={(e) => set("expected_farrowing_date", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.bae7d5be7082")}><Select value={f.gestation_status || "active"} onValueChange={(v) => set("gestation_status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["active", "overdue", "completed", "failed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></Row>
                </div>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    farrowing: {
        title: "Farrowing Record", description: "Record litter outcome and create piglets.",
        path: "farrowing-records",
        validate: (f) => {
            if (!f.dam_id) return "Dam animal ID is required";
            if (!f.date) return "Farrowing date is required";
            const live = Number(f.live_born || 0);
            const still = Number(f.stillborn || 0);
            if (!Number.isFinite(live) || live < 0) return "Live born must be 0 or greater";
            if (!Number.isFinite(still) || still < 0) return "Stillborn must be 0 or greater";
            return null;
        },
        transform: (f) => ({
            dam_id: f.dam_id,
            animal_id: f.dam_id,
            date: f.date,
            live_born: Number(f.live_born || 0),
            stillborn: Number(f.stillborn || 0),
            mummified: Number(f.mummified || 0),
            weak_piglets: Number(f.weak_piglets || 0),
            birth_weight: f.birth_weight === "" ? null : Number(f.birth_weight),
            complications: f.complications || "",
            assistance_required: f.assistance_required || "",
            notes: f.notes || "",
        }),
        fields: (f, set) => (
            <>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.a539194be231")}><Input value={f.dam_id} onChange={(e) => set("dam_id", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.d36f1955ad71")}><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Row>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.93de82546383")}><Input type="number" min="0" value={f.live_born || ""} onChange={(e) => set("live_born", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.3a3a224571d8")}><Input type="number" min="0" value={f.stillborn || ""} onChange={(e) => set("stillborn", e.target.value)} /></Row>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.88373169e5fa")}><Input type="number" min="0" value={f.mummified || ""} onChange={(e) => set("mummified", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.439ff8e6fc5a")}><Input type="number" min="0" value={f.weak_piglets || ""} onChange={(e) => set("weak_piglets", e.target.value)} /></Row>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.021f7ccb8d3d")}><Input type="number" step="0.01" min="0" value={f.birth_weight || ""} onChange={(e) => set("birth_weight", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.be6b37bac29a")}><Input value={f.assistance_required || ""} onChange={(e) => set("assistance_required", e.target.value)} placeholder={i18n.t("batch4.e5efd304560a")} /></Row>
                </div>
                <Row label={i18n.t("batch4.5e2ba821e480")}><Input value={f.complications || ""} onChange={(e) => set("complications", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    vaccination_schedule: {
        title: "Schedule Vaccination", description: "Create a vaccination schedule with reminders.",
        path: "medical-schedules",
        validate: (f) => (!f.schedule_name ? "Vaccine name required" : !f.scheduled_date ? "Scheduled date required" : null),
        transform: (f) => ({
            animal_id: f.animal_id,
            schedule_type: "vaccination",
            schedule_name: f.schedule_name,
            dose: f.dose || "",
            route: f.route || "",
            scheduled_date: f.scheduled_date,
            repeat_interval_days: Number(f.repeat_interval_days || 0),
            next_due_date: f.next_due_date || "",
            assigned_employee_id: f.assigned_employee_id || "",
            assigned_employee_name: f.assigned_employee_name || "",
            priority: f.priority || "medium",
            inventory_item_id: f.inventory_item_id || "",
            quantity_used: Number(f.quantity_used || 0),
            notes: f.notes || "",
        }),
        fields: (f, set) => (
            <>
                <Row label={i18n.t("batch4.c110df7ef9e2")}><Input value={f.schedule_name} onChange={(e) => set("schedule_name", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.da7f99e8cbbf")}><Input value={f.dose} onChange={(e) => set("dose", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.4999528efe0f")}><Input value={f.route} onChange={(e) => set("route", e.target.value)} /></Row>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.b41cf063eca8")}><Input type="date" value={f.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.5aab6f03b04f")}><Input type="date" value={f.next_due_date || ""} onChange={(e) => set("next_due_date", e.target.value)} /></Row>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.7fbf37a3cf8e")}><Input type="number" value={f.repeat_interval_days || ""} onChange={(e) => set("repeat_interval_days", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.886cbff9d9df")}><Select value={f.priority} onValueChange={(v) => set("priority", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["low", "medium", "high", "critical"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></Row>
                </div>
                <Row label={i18n.t("batch4.e0cd87951b95")}><Input value={f.assigned_employee_id || ""} onChange={(e) => set("assigned_employee_id", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.173a5dadf4e7")}><Input value={f.assigned_employee_name || ""} onChange={(e) => set("assigned_employee_name", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.df280e8d1006")}><Input value={f.inventory_item_id || ""} onChange={(e) => set("inventory_item_id", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.01983f26074b")}><Input type="number" step="0.01" value={f.quantity_used || ""} onChange={(e) => set("quantity_used", e.target.value)} /></Row>
                </div>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    treatment_schedule: {
        title: "Schedule Treatment", description: "Create a treatment/deworming follow-up schedule.",
        path: "medical-schedules",
        validate: (f) => (!f.schedule_name ? "Treatment name required" : !f.scheduled_date ? "Scheduled date required" : null),
        transform: (f) => ({
            animal_id: f.animal_id,
            schedule_type: "treatment",
            schedule_name: f.schedule_name,
            dose: f.dose || "",
            route: f.route || "",
            scheduled_date: f.scheduled_date,
            repeat_interval_days: Number(f.repeat_interval_days || 0),
            next_due_date: f.next_due_date || "",
            assigned_employee_id: f.assigned_employee_id || "",
            assigned_employee_name: f.assigned_employee_name || "",
            priority: f.priority || "medium",
            inventory_item_id: f.inventory_item_id || "",
            quantity_used: Number(f.quantity_used || 0),
            notes: f.notes || "",
        }),
        fields: (f, set) => (
            <>
                <Row label={i18n.t("batch4.3a9bf18192a7")}><Input value={f.schedule_name} onChange={(e) => set("schedule_name", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.da7f99e8cbbf")}><Input value={f.dose} onChange={(e) => set("dose", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.4999528efe0f")}><Input value={f.route} onChange={(e) => set("route", e.target.value)} /></Row>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.b41cf063eca8")}><Input type="date" value={f.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.5aab6f03b04f")}><Input type="date" value={f.next_due_date || ""} onChange={(e) => set("next_due_date", e.target.value)} /></Row>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.7fbf37a3cf8e")}><Input type="number" value={f.repeat_interval_days || ""} onChange={(e) => set("repeat_interval_days", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.886cbff9d9df")}><Select value={f.priority} onValueChange={(v) => set("priority", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["low", "medium", "high", "critical"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></Row>
                </div>
                <Row label={i18n.t("batch4.e0cd87951b95")}><Input value={f.assigned_employee_id || ""} onChange={(e) => set("assigned_employee_id", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.173a5dadf4e7")}><Input value={f.assigned_employee_name || ""} onChange={(e) => set("assigned_employee_name", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.df280e8d1006")}><Input value={f.inventory_item_id || ""} onChange={(e) => set("inventory_item_id", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.01983f26074b")}><Input type="number" step="0.01" value={f.quantity_used || ""} onChange={(e) => set("quantity_used", e.target.value)} /></Row>
                </div>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    treatment: {
        title: "Log Treatment", description: "Record medicine given and any withdrawal period.",
        path: "treatments",
        allowPhoto: true,
        validate: (f) => (!f.medicine ? "Medicine required" : !f.issue ? "Issue / diagnosis required" : null),
        transform: (f) => ({ ...f, medicine_name: f.medicine || f.medicine_name || "", treatment_type: "medication" }),
        fields: (f, set, { isOwner }) => (
            <>
                <Row label={i18n.t("batch4.eb9a4bc1c0c1")}><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.50282362745d")}><Input value={f.issue} onChange={(e) => set("issue", e.target.value)} data-testid="tx-issue-input" placeholder={i18n.t("batch4.f3355e26c1d6")} /></Row>
                <Row label={i18n.t("batch4.8cd9373697bb")}><Input value={f.symptoms || ""} onChange={(e) => set("symptoms", e.target.value)} placeholder={i18n.t("batch4.69b9aa277782")} /></Row>
                <Row label={i18n.t("batch4.3be0c08a7833")}><Input value={f.diagnosis || ""} onChange={(e) => set("diagnosis", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.b33fc83f3d94")}><Input value={f.medicine} onChange={(e) => set("medicine", e.target.value)} data-testid="tx-medicine-input" /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.fbd71ca491cf")}><Input value={f.dosage} onChange={(e) => set("dosage", e.target.value)} placeholder={i18n.t("batch4.51cfe939b512")} /></Row>
                    <Row label={i18n.t("batch4.4999528efe0f")}><Select value={f.route} onValueChange={(v) => set("route", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["oral", "im", "sc", "iv", "topical"].map((r) => <SelectItem key={r} value={r}>{r.toUpperCase()}</SelectItem>)}</SelectContent></Select></Row>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.ff99f5b5f794")}><Input type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.89d10cd6c1e1")}><Input type="date" value={f.end_date || ""} onChange={(e) => set("end_date", e.target.value)} /></Row>
                </div>
                <Row label={i18n.t("batch4.f219cc0614ae")}><Input value={f.reason || ""} onChange={(e) => set("reason", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.9e50be33c302")}><Input value={f.administered_by || ""} onChange={(e) => set("administered_by", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.b77d3190bd45")}><Input type="date" value={f.withdrawal_end_date} onChange={(e) => set("withdrawal_end_date", e.target.value)} data-testid="tx-withdrawal-input" /></Row>
                    {isOwner ? <Row label={i18n.t("batch4.0948a33098e0")}><Input type="number" value={f.cost} onChange={(e) => set("cost", e.target.value)} /></Row> : null}
                </div>
                <Row label={i18n.t("batch4.bae7d5be7082")}><Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="tx-status-select"><SelectValue /></SelectTrigger><SelectContent>{["under_observation", "under_treatment", "recovering", "recovered", "referred_vet", "chronic", "deceased"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></Row>
                <Row label={i18n.t("batch4.8cbaf00d9257")}><Select value={f.recovery_status || "under_treatment"} onValueChange={(v) => set("recovery_status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["under_treatment", "recovering", "recovered", "chronic", "relapse"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></Row>
                <Row label={i18n.t("batch4.b484d4783e79")}><Textarea rows={2} value={f.vet_notes || ""} onChange={(e) => set("vet_notes", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    weight: {
        title: "Record Weight", description: "Add a new weight measurement.",
        path: "weight-records",
        validate: (f) => (!f.weight ? "Weight required" : null),
        transform: (f) => ({ ...f, weight: Number(f.weight) }),
        fields: (f, set, { isOwner }) => (
            <>
                <Row label={i18n.t("batch4.eb9a4bc1c0c1")}><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.23f77f32f50d")}><Input type="number" step="0.01" value={f.weight} onChange={(e) => set("weight", e.target.value)} data-testid="wt-weight-input" /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.453f30793bb5")}><Input value={f.body_condition} onChange={(e) => set("body_condition", e.target.value)} placeholder="1-5" /></Row>
                    <Row label={i18n.t("batch4.88306943fea7")}><Select value={f.method} onValueChange={(v) => set("method", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["scale", "tape", "visual", "estimated"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></Row>
                </div>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    movement: {
        title: "Log Movement", description: "Move animal between pens / paddocks.",
        path: "animal-movements",
        validate: (f) => (!f.to_location ? "Destination required" : null),
        fields: (f, set) => (
            <>
                <Row label={i18n.t("batch4.eb9a4bc1c0c1")}><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.3f66052a107e")}><Input value={f.from_location} onChange={(e) => set("from_location", e.target.value)} data-testid="mv-from-input" placeholder={i18n.t("batch4.4c9358d080ad")} /></Row>
                <Row label={i18n.t("batch4.c4d9d145efc1")}><Input value={f.to_location} onChange={(e) => set("to_location", e.target.value)} data-testid="mv-to-input" placeholder={i18n.t("batch4.f0cb1033c6f1")} /></Row>
                <Row label={i18n.t("batch4.f219cc0614ae")}><Input value={f.reason} onChange={(e) => set("reason", e.target.value)} placeholder={i18n.t("batch4.b7504d4a57aa")} /></Row>
                <Row label={i18n.t("batch4.a4730a22cf49")}><Input value={f.staff} onChange={(e) => set("staff", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    mortality: {
        title: "Record Mortality", description: "Log the death of this animal (also updates status).",
        path: "mortality-records",
        validate: (f) => (!f.cause ? "Cause required" : null),
        transform: (f) => ({ ...f, financial_loss: Number(f.financial_loss || 0) }),
        fields: (f, set) => (
            <>
                <Row label={i18n.t("batch4.eb9a4bc1c0c1")}><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.eb5e474d1376")}><Input value={f.cause} onChange={(e) => set("cause", e.target.value)} data-testid="mo-cause-input" placeholder={i18n.t("batch4.6fa926620c18")} /></Row>
                <Row label={i18n.t("batch4.08c30fcc04d3")}><Input value={f.suspected} onChange={(e) => set("suspected", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.5fad6696d4de")}><Select value={f.disposal_method} onValueChange={(v) => set("disposal_method", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["buried", "incinerated", "compost", "vet_lab", "other"].map((d) => <SelectItem key={d} value={d} className="capitalize">{d.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></Row>
                    <Row label={i18n.t("batch4.ffcd11eb56e9")}><Input type="number" value={f.financial_loss} onChange={(e) => set("financial_loss", e.target.value)} /></Row>
                </div>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    reservation: {
        title: "Reserve for Buyer", description: "Hold the animal for a buyer with optional deposit.",
        path: "reservations",
        validate: (f) => (!f.buyer_name ? "Buyer name required" : null),
        transform: (f) => ({ ...f, price: Number(f.price || 0), deposit: Number(f.deposit || 0), status: "active" }),
        fields: (f, set) => (
            <>
                <Row label={i18n.t("batch4.a85dbb248b0c")}><Input value={f.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} data-testid="rs-buyer-input" /></Row>
                <Row label={i18n.t("batch4.713032d9c7a1")}><Input value={f.buyer_phone} onChange={(e) => set("buyer_phone", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.11d56ea29108")}><Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.1904ed248a58")}><Input type="number" value={f.deposit} onChange={(e) => set("deposit", e.target.value)} /></Row>
                </div>
                <Row label={i18n.t("batch4.055357a31ca6")}><Input type="date" value={f.target_delivery_date} onChange={(e) => set("target_delivery_date", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    vaccination: {
        title: "Record Vaccination", description: "Add a vaccination record.",
        path: "vaccination-records",
        allowPhoto: true,
        validate: (f) => (!f.vaccine ? "Vaccine required" : null),
        transform: (f) => ({
            ...f,
            vaccine_name: f.vaccine || f.vaccine_name || "",
            date_administered: f.date_given || f.scheduled_date,
            next_due_date: f.next_due_date || f.scheduled_date || "",
            cost: Number(f.cost || 0),
            dose: f.dose || "1",
        }),
        fields: (f, set, { isOwner }) => (
            <>
                <Row label={i18n.t("batch4.226c7cd5e42b")}><Input value={f.vaccine} onChange={(e) => set("vaccine", e.target.value)} data-testid="vc-vaccine-input" /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.1cd1bdad468f")}><Input type="date" value={f.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.893ea9eb180d")}><Input type="date" value={f.date_given} onChange={(e) => set("date_given", e.target.value)} /></Row>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.63ad1e932301")}><Input value={f.batch_number || ""} onChange={(e) => set("batch_number", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.7adfcd13b3b0")}><Input value={f.manufacturer || ""} onChange={(e) => set("manufacturer", e.target.value)} /></Row>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.5aab6f03b04f")}><Input type="date" value={f.next_due_date || ""} onChange={(e) => set("next_due_date", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.6b440cd50680")}><Input type="date" value={f.expiry_date || ""} onChange={(e) => set("expiry_date", e.target.value)} /></Row>
                </div>
                <Row label={i18n.t("batch4.1eda23758be9")}><Input value={f.administrator || ""} onChange={(e) => set("administrator", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.da7f99e8cbbf")}><Input value={f.dose} onChange={(e) => set("dose", e.target.value)} /></Row>
                    {isOwner ? <Row label={i18n.t("batch4.0948a33098e0")}><Input type="number" value={f.cost} onChange={(e) => set("cost", e.target.value)} /></Row> : null}
                </div>
                <Row label={i18n.t("batch4.bae7d5be7082")}><Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["upcoming", "due_today", "overdue", "completed", "missed", "cancelled"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></Row>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    health: {
        title: "Health Check", description: "Log observation or health issue.",
        path: "health-records",
        allowPhoto: true,
        validate: (f) => (!f.issue ? "Issue / observation required" : null),
        fields: (f, set) => (
            <>
                <Row label={i18n.t("batch4.eb9a4bc1c0c1")}><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.652d370d2331")}><Input value={f.issue} onChange={(e) => set("issue", e.target.value)} data-testid="hl-issue-input" /></Row>
                <Row label={i18n.t("batch4.8cd9373697bb")}><Input value={f.symptoms || ""} onChange={(e) => set("symptoms", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.3be0c08a7833")}><Input value={f.diagnosis || ""} onChange={(e) => set("diagnosis", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.453f30793bb5")}><Input value={f.body_condition} onChange={(e) => set("body_condition", e.target.value)} placeholder="1-5" /></Row>
                    <Row label={i18n.t("batch4.29a37cb1e915")}><Input type="number" step="0.1" value={f.temperature} onChange={(e) => set("temperature", e.target.value)} /></Row>
                </div>
                <Row label={i18n.t("batch4.bae7d5be7082")}><Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["under_observation", "under_treatment", "recovering", "recovered", "critical"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></Row>
                <Row label={i18n.t("batch4.8cbaf00d9257")}><Select value={f.recovery_status || "under_observation"} onValueChange={(v) => set("recovery_status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["under_observation", "under_treatment", "recovering", "recovered", "critical"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></Row>
                <Row label={i18n.t("batch4.b484d4783e79")}><Textarea rows={2} value={f.vet_notes || ""} onChange={(e) => set("vet_notes", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    deworming: {
        title: "Record Deworming", description: "Log deworming treatment details.",
        path: "treatments",
        allowPhoto: true,
        validate: (f) => (!f.medicine_name ? "Treatment required" : null),
        transform: (f) => ({ ...f, medicine: f.medicine_name || "", treatment_type: "deworming" }),
        fields: (f, set) => (
            <>
                <Row label={i18n.t("batch4.3a9bf18192a7")}><Input value={f.medicine_name || ""} onChange={(e) => set("medicine_name", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.fbd71ca491cf")}><Input value={f.dosage || ""} onChange={(e) => set("dosage", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.4999528efe0f")}><Input value={f.route || ""} onChange={(e) => set("route", e.target.value)} /></Row>
                </div>
                <Row label={i18n.t("batch4.f219cc0614ae")}><Input value={f.reason || ""} onChange={(e) => set("reason", e.target.value)} /></Row>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.ff99f5b5f794")}><Input type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></Row>
                    <Row label={i18n.t("batch4.89d10cd6c1e1")}><Input type="date" value={f.end_date || ""} onChange={(e) => set("end_date", e.target.value)} /></Row>
                </div>
                <Row label={i18n.t("batch4.247b5cbc58fd")}><Input type="date" value={f.withdrawal_end_date || ""} onChange={(e) => set("withdrawal_end_date", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.9e50be33c302")}><Input value={f.administered_by || ""} onChange={(e) => set("administered_by", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    vet_visit: {
        title: "Record Vet Visit", description: "Log consultation findings and treatment plan.",
        path: "treatments",
        allowPhoto: true,
        validate: (f) => (!f.veterinarian ? "Veterinarian required" : null),
        transform: (f) => ({ ...f, treatment_type: "vet_visit", issue: f.findings || f.issue || "", medicine: f.visit_type || "vet_visit" }),
        fields: (f, set) => (
            <>
                <Row label={i18n.t("batch4.b3c57561b101")}><Select value={f.visit_type || "consultation"} onValueChange={(v) => set("visit_type", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["consultation", "emergency", "follow_up"].map((v) => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></Row>
                <Row label={i18n.t("batch4.0ea2a19f5694")}><Input value={f.veterinarian || ""} onChange={(e) => set("veterinarian", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.ca9b7e7e1f0d")}><Textarea rows={2} value={f.findings || ""} onChange={(e) => set("findings", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.5738f8965a57")}><Textarea rows={2} value={f.treatment_plan || ""} onChange={(e) => set("treatment_plan", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.b8da11963e47")}><Input type="date" value={f.follow_up_date || ""} onChange={(e) => set("follow_up_date", e.target.value)} /></Row>
                <Row label={i18n.t("batch4.70440046a3dc")}><Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Row>
            </>
        ),
    },
    expense: {
        title: "Add Expense", description: "Feed, medicine, or other cost allocated to this animal.",
        path: "animal-expenses",
        validate: (f) => (!f.amount || Number(f.amount) <= 0 ? "Positive amount required" : null),
        transform: (f) => ({ ...f, amount: Number(f.amount) }),
        fields: (f, set) => (
            <>
                <div className="grid grid-cols-2 gap-2">
                    <Row label={i18n.t("batch4.a3c686e711e4")}><Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="ex-category-select"><SelectValue /></SelectTrigger><SelectContent>{["feed", "medicine", "vaccination", "treatment", "labour", "transport", "other"].map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent></Select></Row>
                    <Row label={i18n.t("batch4.eb9a4bc1c0c1")}><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Row>
                </div>
                <Row label={i18n.t("batch4.b46706354351")}><Input type="number" step="0.01" value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="ex-amount-input" /></Row>
                <Row label={i18n.t("batch4.55f8ebc805e6")}><Input value={f.description} onChange={(e) => set("description", e.target.value)} /></Row>
            </>
        ),
    },
};
